"use client"

import { useState, useEffect, useMemo } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ArrowRight, GitCompare, HelpCircle } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import {
  captureAnchor,
  markBeforeRedirect,
  useRestoreScrollAfterOAuth,
} from "@/hooks/useGuideScrollRestore"
import { AnimatedBackground } from "@/components/ui/animated-background"
import { ReadingProgress } from "@/components/ui/reading-progress"
import { StickySidebarTOC } from "@/components/ui/sticky-sidebar-toc"
import { StickySidebarCTA } from "@/components/blog/sticky-sidebar-cta"
import { SidebarToolLinks } from "@/components/blog/sidebar-tool-links"
import { GuideSidebarRailExperiment } from "@/components/guides/guide-sidebar-rail-experiment"
import { GuidePhotoHero } from "@/components/guides/guide-photo-hero"
import { GuideStatStrip } from "@/components/guides/guide-stat-strip"
import { GuideEmailGate } from "@/components/guides/guide-email-gate"
import { ContentNewsletterModal } from "@/components/newsletter/content-newsletter-modal"
import { GuideToolCallout } from "@/components/guides/guide-tool-callout"
import { GuideQuickStart } from "@/components/guides/guide-quick-start"
import { PillarKeyTakeaways } from "@/components/guides/shared/pillar-key-takeaways"
import { PillarSources } from "@/components/guides/shared/pillar-sources"
import { PillarCompareCard } from "@/components/guides/shared/pillar-compare-card"
import { GuideProductCard } from "@/components/guides/guide-product-card"
import { GuideLandingCard } from "@/components/guides/guide-landing-card"
import { CareTaskLinksForGrass } from "@/components/guides/pillar-cross-links"
import { BlogCard } from "@/components/ui/blog-card"
import { getProductsForSection, getSidebarProducts, getTopSectionsForGuide, type ProductRecommendation } from "@/data/guide-product-recommendations"
import type { GrassGuide } from "@/data/guide-content"
import { getGuideBySlug, type GuideMetadata } from "@/data/guide-slugs"
import type { GrassCharacteristics } from "@/lib/types/recommendation-engine"
import type { Author } from "@/lib/types/author"
import { communityAnalytics } from "@/lib/analytics"
import { ReminderWidget } from "@/components/blog/reminder-widget"
import { GrassReferenceGallery } from "@/components/guides/grass-reference-gallery"
import { getGrassReferenceImages } from "@/data/grass-reference-photos"
import { ImageCreditsFooter } from "@/components/ui/image-credits-footer"
import { creditFromPhotoRecord, parseImageCreditText } from "@/lib/image-credits"

import { AD_CONTENT_ROOT_ID } from "@/lib/ads/content-root"
interface RelatedGuide {
  metadata: GuideMetadata
  grassData: GrassCharacteristics
}

interface SatellitePost {
  id: string
  title: string
  slug: string
  excerpt: string
  category: string
  image: string
  date_published: string
  reading_time: number
}

interface RelatedComparison {
  slug: string
  shortLabel: string
  metaDescription: string
}

interface GuidePageContentProps {
  guide: GrassGuide
  meta: GuideMetadata
  grassData: GrassCharacteristics
  allContentHtml: string
  author: Author
  relatedGuides: RelatedGuide[]
  relatedComparisons?: RelatedComparison[]
  satellitePosts?: SatellitePost[]
  readingMinutes?: number
  /** Admin-managed hero image URL (Directus). Null = brand-gradient fallback. */
  heroImageUrl?: string | null
  /** Hero image credit; image tooltip + footer credit line only. */
  heroImageCredit?: string | null
}

function isDevEnvironment(): boolean {
  if (typeof window === "undefined") return false
  const hostname = window.location.hostname
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("192.168.")
}

export function GuidePageContent({
  guide,
  meta,
  grassData,
  allContentHtml,
  author,
  relatedGuides,
  relatedComparisons = [],
  satellitePosts = [],
  readingMinutes,
  heroImageUrl,
  heroImageCredit,
}: GuidePageContentProps) {
  const [isUnlocked, setIsUnlocked] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const { user } = useAuth()
  const pathname = usePathname()

  // guide_viewed is emitted by <GuideViewTracker> in app/guides/[slug]/page.tsx
  // so all four guide render branches count identically.

  useEffect(() => {
    // Skip gate entirely in dev environment
    if (isDevEnvironment()) {
      setIsUnlocked(true)
      setIsHydrated(true)
      return
    }
    const subscribed = localStorage.getItem("wgit_guide_subscribed") === "true"
    const unlocked = subscribed || !!user
    setIsUnlocked(unlocked)
    // Persist unlock for authenticated users so it survives logout
    if (user && !subscribed) {
      localStorage.setItem("wgit_guide_subscribed", "true")
    }
    setIsHydrated(true)
  }, [user])

  // Find the index of the first gated section
  const firstGatedIndex = guide.sections.findIndex((s) => s.gated)
  const showGate = isHydrated && !isUnlocked && firstGatedIndex !== -1

  const handleUnlock = () => {
    // Capture the gate's first gated section as anchor BEFORE the unlock
    // re-renders the tree. After React commits, re-anchor scrollY so the
    // user's viewport stays on the same DOM area.
    const anchorId =
      firstGatedIndex !== -1 ? guide.sections[firstGatedIndex].id : null
    const anchorEl = anchorId ? document.getElementById(anchorId) : null
    const restore = captureAnchor(anchorEl)

    localStorage.setItem("wgit_guide_subscribed", "true")
    setIsUnlocked(true)
    restore()
  }

  // Restore scroll position after social-OAuth round-trip reload.
  useRestoreScrollAfterOAuth({ isHydrated, isUnlocked, pathname: pathname || "" })

  // Pre-compute which sections show product cards: one card per priority section
  // (up to 4 per guide — soil-ph, fertilizer, seeding, weed-control), max 2 products each.
  const sectionProducts = useMemo(() => {
    const prioritySections = getTopSectionsForGuide(guide.slug)
    const map = new Map<string, ProductRecommendation[]>()
    for (const sectionId of prioritySections) {
      const products = getProductsForSection(guide.slug, sectionId)
      if (products.length > 0) {
        map.set(sectionId, products)
      }
    }
    return map
  }, [guide.slug])

  // Early, ABOVE-THE-GATE affiliate CTA. The in-section product cards live inside
  // gated sections (soil-ph/fertilizer/seeding/weed-control), so ungated visitors
  // never see them — the reason guides earn ~0 affiliate clicks. This surfaces the
  // core "shopping list for a <grass> lawn" (seed → feed → weed) right after the
  // Quick Start, deduped, so every reader sees it. Tracked as `guide-early`.
  const earlyProducts = useMemo(() => {
    const picks: ProductRecommendation[] = []
    const seen = new Set<string>()
    for (const sectionId of ['seeding', 'fertilizer', 'weed-control']) {
      const first = getProductsForSection(guide.slug, sectionId)[0]
      if (first && !seen.has(first.affiliateUrl)) {
        seen.add(first.affiliateUrl)
        picks.push(first)
      }
    }
    return picks
  }, [guide.slug])

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <ReadingProgress />

      <div className="relative">
        {/* Editorial full-bleed photo hero. The visual breadcrumb is
            decorative; the BreadcrumbList JSON-LD is emitted from page.tsx. */}
        <GuidePhotoHero
          guide={guide}
          meta={meta}
          author={author}
          readingMinutes={readingMinutes}
          heroImageUrl={heroImageUrl}
          imageCredit={heroImageCredit}
        />

        {/* Main Content Grid — matches BlogPostMainGrid pattern */}
        <div className="relative px-4 py-2 md:py-3 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 gap-6 lg:gap-8 xl:grid-cols-[2fr_8fr_2fr]">
              {/* Desktop Sidebar TOC */}
              <aside className="hidden xl:sticky xl:top-24 xl:block xl:self-start">
                <StickySidebarTOC content={allContentHtml} />
              </aside>

              {/* Main Content — open editorial column on the light animated
                  background (no opaque card), matching the blog post layout. */}
              <div>
                {/* Relocated tolerance stats, just below the hero */}
                <GuideStatStrip grassData={grassData} />

                {/* Editorial lede with drop-cap, from the hero subtitle */}
                <div className="prose-lede prose-lede-host mb-6 md:mb-8">
                  <p>{guide.heroSubtitle}</p>
                </div>

                <div className="relative">
                  <div>
                    {/* Key takeaways — scannable summary for AI-search citation
                        and human skim-readers. Renders only when authored. */}
                    {guide.keyTakeaways && guide.keyTakeaways.length > 0 && (
                      <div className="mb-6 md:mb-8">
                        <PillarKeyTakeaways items={guide.keyTakeaways} />
                      </div>
                    )}

                    {/* Compare against a peer grass (deep-links into the
                        grass-comparison tool with both grasses preselected). */}
                    <div className="mb-6 md:mb-8">
                      <PillarCompareCard
                        grassSlug={guide.slug}
                        grassDisplay={meta.displayName}
                      />
                    </div>

                    {/* Early, above-the-gate affiliate CTA. In-section product cards
                        sit inside gated sections (invisible to non-subscribers), so this
                        is the sole ungated shopping surface — it carried ~10% CTR but only
                        ~250 impressions in 8 days (PostHog Jul 1-9) because it sat below the
                        hero, takeaways, compare card AND the monthly to-do. Raised above
                        GuideQuickStart (still under the intro cards, never under a heading)
                        to lift impressions without displacing the scannable takeaways. */}
                    {earlyProducts.length > 0 && (
                      <div className="mb-6 md:mb-8">
                        <GuideProductCard
                          guideSlug={guide.slug}
                          products={earlyProducts}
                          placement="guide-early"
                          title={`What you need for a ${meta.displayName} lawn`}
                          sectionId="early"
                          railHost="guide"
                        />
                      </div>
                    )}

                    {/* Quick Start: What to do this month */}
                    <GuideQuickStart grassKey={guide.grassKey} season={meta.season} />

                    {/* Guide sections — each section gets its own prose context
                        so tool callouts and product cards render outside prose styles */}
                    {/* overflow-x-hidden: guard against oversized injected
                        desktop ad units (ad-layout audit 2026-07-31). */}
                    {/* `relative` + the `.px-0 > .relative` pair below are NOT
                        styling — they reproduce the DOM shape Mediavine's
                        content_selector targets:

                          "article.relative .px-0 > .relative"

                        Journey does not read a slot list from us; their ad-ops
                        hand-wrote that one selector against the BLOG template at
                        onboarding, so guides matched it ZERO times and served no
                        in-content ads at all — only the anchor bar (measured on
                        prod 2026-08-03: `document.querySelectorAll(sel).length`
                        was 0 here and 1 on a blog post). All three classes are
                        visually inert: `relative` has no absolutely-positioned
                        children at this level, and `px-0` zeroes padding the div
                        never had.

                        The inner `.relative` is the content root. With
                        `enable_divbuster: false` (also in their live config) the
                        scanner does not descend into div children, so ads land
                        only BETWEEN the per-section wrappers below — never
                        between a section's prose and its GuideToolCallout or
                        GuideProductCard, because those are nested inside the
                        section div. That is the same protection
                        lib/ad-scanner-guard.ts has to synthesize for the blog,
                        which is why guides need no equivalent guard.

                        FRAGILE BY CONSTRUCTION: renaming these classes silently
                        kills in-content ads on all 21 guides with no error
                        anywhere. Ask Journey support to target a stable id
                        instead — see obsidian/features/mediavine-journey-ads.md.

                        2026-08-21: their ops changed the live selector to
                        "article .prose-lede-host" (observed in the served
                        model; the old selector matched here but served zero
                        units). The content root now carries that class too —
                        inert at this level: its children are the per-section
                        divs, so the lede drop-cap CSS (`> p:first-of-type`)
                        cannot fire. Both hooks stay so neither selector
                        generation kills guide ads. */}
                    <article className="relative max-w-none overflow-x-hidden">
                      <div className="px-0">
                      <div className="relative prose-lede-host" id={AD_CONTENT_ROOT_ID}>
                      {guide.sections.map((section, index) => {
                        const isGated = section.gated
                        const isFirstGated = index === firstGatedIndex
                        const shouldBlur = isGated && showGate
                        // Gated sections AFTER the first one used to render here
                        // with their content in a `.hidden` div — which left an
                        // empty section wrapper as a direct child of the ad
                        // content root. Several near-zero-height boundaries in a
                        // row is where Mediavine would stack units on a gated
                        // view. They now render in the `.hidden` sibling BELOW
                        // this container instead: same markup, same SEO
                        // visibility to crawlers, outside the matched node so
                        // the scanner never sees them as boundaries.
                        if (shouldBlur && !isFirstGated) return null
                        // Lead recognition photo + every curated alternate.
                        // Same list the guide ImageObject nodes and
                        // sitemap-image.xml declare, so nothing is listed that
                        // the reader cannot see.
                        const idImages =
                          section.id === "identification"
                            ? getGrassReferenceImages(guide.grassKey)
                            : []

                        return (
                          <div key={section.id}>
                            <div
                              id={section.id}
                              className={`prose-enhanced${index === 0 ? ' [&>h2:first-child]:mt-0' : ''}${
                                shouldBlur
                                  ? " relative select-none pointer-events-none"
                                  : ""
                              }`}
                            >
                              {shouldBlur && isFirstGated && (
                                <div className="relative overflow-hidden max-h-[220px]">
                                  <h2>{section.title}</h2>
                                  <div
                                    className="blur-[6px]"
                                    dangerouslySetInnerHTML={{
                                      __html: section.content,
                                    }}
                                  />
                                  <div className="absolute bottom-0 left-0 right-0 h-48 bg-linear-to-b from-transparent via-white/90 to-white pointer-events-none" />
                                </div>
                              )}
                              {!shouldBlur && (
                                <>
                                  <h2>{section.title}</h2>
                                  {idImages.length > 0 && (
                                    <GrassReferenceGallery
                                      images={idImages}
                                      grassName={meta.displayName}
                                    />
                                  )}
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: section.content,
                                    }}
                                  />
                                </>
                              )}
                            </div>

                            {/* Tool callouts after section — outside prose-enhanced */}
                            {!shouldBlur && section.toolLinks?.map((link, i) => (
                              <GuideToolCallout
                                key={i}
                                href={link.href}
                                label={link.label}
                                description={link.description}
                              />
                            ))}

                            {!shouldBlur && section.id === "identification" && (
                              <GuideToolCallout
                                href={`/grass-atlas/${guide.slug}?source=guide`}
                                label={`Explore ${meta.displayName} in 3D`}
                                description="Turn the model, inspect its blades, roots, runners, and seedhead, then compare the grass with close lookalikes."
                                variant="grass-atlas"
                                actionLabel="Open 3D model"
                              />
                            )}

                            {/* Product recommendations — outside prose-enhanced, max 2 products per section card */}
                            {!shouldBlur && sectionProducts.has(section.id) && (
                              <GuideProductCard
                                guideSlug={guide.slug}
                                products={sectionProducts.get(section.id)!}
                                placement="guide-section"
                                sectionId={section.id}
                                railHost="guide"
                              />
                            )}
                          </div>
                        )
                      })}
                      </div>

                      {/* Gated SEO tail — the still-locked sections, kept in the
                          DOM verbatim for crawlers but rendered OUTSIDE the
                          matched `.relative` ad root above. Deliberately a
                          SIBLING inside `.px-0`, and deliberately NOT carrying
                          `relative`, so `article.relative .px-0 > .relative`
                          still resolves to exactly one node. Collapsing the tail
                          into a single `.hidden` container also removes the row
                          of zero-height boundaries a gated view used to present
                          to the ad scanner. */}
                      {showGate && (
                        <div className="hidden">
                          {guide.sections.map((section, index) =>
                            section.gated && index !== firstGatedIndex ? (
                              <div key={section.id} id={section.id} className="prose-enhanced">
                                <h2>{section.title}</h2>
                                <div dangerouslySetInnerHTML={{ __html: section.content }} />
                              </div>
                            ) : null,
                          )}
                        </div>
                      )}
                      </div>
                    </article>
                  </div>
                </div>

                {/* Email Gate Overlay */}
                {showGate && (
                  <div className="-mt-4">
                    <GuideEmailGate
                      grassType={guide.grassKey}
                      grassName={meta.displayName}
                      onUnlock={handleUnlock}
                      onBeforeOAuthRedirect={markBeforeRedirect}
                    />
                  </div>
                )}

                {/* Care tasks for this grass — pillar-to-pillar interlink so
                    a grass-guide reader can pivot to fertilizing, watering,
                    mowing, or weed-control for the cross-cutting how-to. */}
                {(isUnlocked || !isHydrated) && (
                  <div className="mt-8 mb-8">
                    <h2 className="text-xl md:text-2xl font-editorial text-gray-900 mb-4 md:mb-6">
                      Care tasks for {meta.displayName}
                    </h2>
                    <CareTaskLinksForGrass grassKey={guide.grassKey} />
                  </div>
                )}

                {(isUnlocked || !isHydrated) && (
                  <aside className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 md:p-6" aria-labelledby="community-lawns-heading">
                    <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-emerald-800">
                      From the Community
                    </p>
                    <h2 id="community-lawns-heading" className="font-editorial text-xl text-gray-950 md:text-2xl">
                      See real {meta.displayName} lawns
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-700 md:text-base">
                      Compare homeowner photos, renovation progress, and regional examples, then share what worked in your own yard.
                    </p>
                    <Link
                      href={`/community?grassType=${encodeURIComponent(guide.grassKey)}`}
                      onClick={() => communityAnalytics.galleryCtaClicked({
                        source: "grass_guide",
                        grassType: guide.grassKey,
                      })}
                      className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
                    >
                      Browse {meta.displayName} lawn photos
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </aside>
                )}

                {/* Related Guides */}
                {relatedGuides.length > 0 && (isUnlocked || !isHydrated) && (
                  <div className="mt-8 mb-8">
                    <h2 className="text-xl md:text-2xl font-editorial text-gray-900 mb-4 md:mb-6">
                      Other {meta.season === "cool" ? "Cool" : "Warm"} Season Guides
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
                      {relatedGuides.map((rg) => (
                        <GuideLandingCard
                          key={rg.metadata.slug}
                          metadata={rg.metadata}
                          grassData={rg.grassData}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Related /compare/[slug] pages featuring this grass */}
                {relatedComparisons.length > 0 && (isUnlocked || !isHydrated) && (
                  <div className="mt-8 mb-8">
                    <h2 className="text-xl md:text-2xl font-editorial text-gray-900 mb-4 md:mb-6">
                      See how {meta.displayName} compares
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
                      {relatedComparisons.map((c) => (
                        <Link
                          key={c.slug}
                          href={`/compare/${c.slug}`}
                          className="group flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-emerald-400 hover:bg-emerald-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-white">
                              <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                            </span>
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700">
                              {c.shortLabel}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {c.metaDescription}
                          </p>
                          <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                            Read the comparison
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Satellite blog posts — deep dives that funnel up to this pillar */}
                {satellitePosts.length > 0 && (isUnlocked || !isHydrated) && (
                  <div className="mt-8 mb-8">
                    <h2 className="text-xl md:text-2xl font-editorial text-gray-900 mb-4 md:mb-6">
                      Related Deep Dives
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                      {satellitePosts.map((post, i) => (
                        <BlogCard key={post.slug} post={post} index={i} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Frequently asked questions — paired with FAQPage JSON-LD
                    emitted from page.tsx for AI-search citation. Kept as
                    prose (not accordion) so answers stay visible for
                    AI-overview readers and human scanners. */}
                {guide.faqs && guide.faqs.length > 0 && (isUnlocked || !isHydrated) && (
                  <div className="mt-8 mb-8">
                    <div className="mb-5 flex items-center gap-2 md:mb-6">
                      <HelpCircle
                        className="h-5 w-5 text-emerald-600"
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                      <h2 className="m-0 text-xl font-editorial text-gray-900 md:text-2xl">
                        Frequently Asked Questions
                      </h2>
                    </div>
                    <div className="divide-y divide-gray-100 rounded-2xl ds-glass px-5 md:px-6">
                      {guide.faqs.map((faq) => (
                        <div key={faq.question} className="py-5 first:pt-5 last:pb-5 md:py-6">
                          <h3 className="m-0 mb-2 text-base font-semibold text-gray-900 md:text-lg">
                            {faq.question}
                          </h3>
                          <p className="m-0 text-[15px] leading-relaxed text-gray-600 md:text-base">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* University extension sources — outbound trust signals and
                    T-of-EEAT. Rendered when guide.sources is populated; the
                    component returns null otherwise. Always visible (no gate)
                    because authority signals help every reader. */}
                {guide.sources && guide.sources.length > 0 && (
                  <PillarSources items={guide.sources} className="mt-8 mb-8 rounded-2xl ds-glass px-5 py-5 md:px-7 md:py-6" />
                )}

                {/* Post-read newsletter capture at the guide-read peak. The
                    reader just finished a full grass guide — the highest-intent
                    moment. Names the grass specifically so the ask reads like a
                    natural continuation (specificity rule + peak-end rule,
                    obsidian/research/growth-design/design-blunders.md).
                    Only renders once the guide is unlocked; readers who hit the
                    email gate have already subscribed, so we skip the duplicate ask. */}
                {(isUnlocked || !isHydrated) && (
                  <div className="mt-2 mb-8">
                    <ReminderWidget
                      variant="seasonal"
                      grassType={meta.displayName}
                      source="guide-read"
                      placement="guide-read"
                      slug={guide.slug}
                    />
                  </div>
                )}
              </div>

              {/* Right sidebar — tools CTA + product picks */}
              <aside
                // NOT sticky: Mediavine's floating video player and desktop
                // adhesion bar sit over this rail at its resting above-fold
                // position, so a sticky card takes impressions it cannot
                // convert. Matches blog rail v2, non-sticky for the same reason.
                className="hidden xl:block xl:self-start space-y-4"
              >
                <StickySidebarCTA slug={guide.slug} pageFamily="guide_post" />
                <SidebarToolLinks category="grass type" />
                <GuideSidebarRailExperiment slug={guide.slug} products={getSidebarProducts(guide.slug)} />
              </aside>
            </div>
          </div>
        </div>

        {/* Passive exit-intent / scroll-up-after-60% email ask; suppressed
            permanently after any capture (incl. the guide gate unlock),
            30-day cooldown after dismissal. Grass guides pass the display
            name; care-task pillars derive a topic from the slug ("weed-control"
            → "weed control") so the modal names the reader's topic. */}
        <ContentNewsletterModal
          source="guide"
          category={getGuideBySlug(guide.slug)?.displayName ?? guide.slug.replace(/-/g, ' ')}
        />
      </div>

      {/* Last in the tree so the server HTML places it just above the site
          footer; on mount it moves below the footer. */}
      <ImageCreditsFooter
        credits={[
          heroImageUrl ? parseImageCreditText(heroImageCredit) : null,
          ...getGrassReferenceImages(guide.grassKey).map(creditFromPhotoRecord),
        ]}
      />
    </div>
  )
}
